/**
 * Resolve the active recipe for a menu dish.
 * Pure lookup — no inventory I/O.
 */

import {
  normalizeRecipe,
  normalizeRecipeStatus,
  RECIPE_STATUS,
  compareRecipeVersions
} from './recipeModel';
import { sameProductId } from '../../../utils/productIdUtils';

/**
 * @param {unknown[]} recipes
 * @param {string|number} dishId
 * @returns {object|null} normalized active recipe or null
 */
export function resolveActiveRecipe(recipes, dishId) {
  if (dishId == null || dishId === '') return null;
  const list = Array.isArray(recipes) ? recipes : [];
  const actives = [];

  for (const raw of list) {
    const recipe = normalizeRecipe(raw);
    if (!recipe) continue;
    if (!sameProductId(recipe.dishId, dishId)) continue;
    if (normalizeRecipeStatus(recipe.status) !== RECIPE_STATUS.ACTIVE) continue;
    actives.push(recipe);
  }

  if (actives.length === 0) return null;
  if (actives.length === 1) return actives[0];

  // Multiple actives (data defect): highest version wins.
  actives.sort((a, b) => compareRecipeVersions(b.version, a.version));
  return actives[0];
}

/**
 * Map dishId → active recipe (one per dish).
 * @param {unknown[]} recipes
 * @returns {Map<string, object>}
 */
export function buildActiveRecipeMap(recipes) {
  const map = new Map();
  const list = Array.isArray(recipes) ? recipes : [];
  for (const raw of list) {
    const recipe = normalizeRecipe(raw);
    if (!recipe || recipe.status !== RECIPE_STATUS.ACTIVE) continue;
    const key = String(recipe.dishId);
    const prev = map.get(key);
    if (!prev || compareRecipeVersions(recipe.version, prev.version) > 0) {
      map.set(key, recipe);
    }
  }
  return map;
}
